import { useEffect, useState } from "react";
import { HeatMap } from "@nivo/heatmap";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListSubheader from "@mui/material/ListSubheader";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import { useQueryComparison } from "../../hooks/top12ApiHooks";
import { useWindowSize } from "../../hooks/windowHooks";
import { useAppStore } from "../../store/appStore";
import { sidebarWidth } from "../Menu";
import { Loading } from "../Loading";
import { pad } from "../../fn/pad";

const ComparePage = () => {
	const { width, height } = useWindowSize();
	const darkModeEnabled = useAppStore((state) => state.darkModeEnabled);
	const listOption = useAppStore((state) => state.listOption);

	const { isLoading, isError, error, data } = useQueryComparison({
		listOption,
	});

	const [localData, setLocalData] = useState([]);

	useEffect(() => {
		if (data) {
			const chartData = data.records.map((choice) => {
				return { id: choice.id, data: choice.compare };
			});

			setLocalData([...chartData]);
		}
	}, [data]);

	if (isLoading || !width || !height) return <Loading />;

	if (isError) return <div>錯誤: {error}</div>;

	if (!data.records) return <div>錯誤: 未能存取資料。</div>;

	const ChartTooltip = ({ cell }) => {
		const { serieId, data: vs, formattedValue } = cell;
		return serieId === vs.x ? null : (
			<Paper sx={{ minWidth: 280 }}>
				<List dense disablePadding>
					<ListSubheader>
						<Stack direction="row" alignItems="center" spacing={2}>
							<ListItemIcon>
								<Avatar
									alt={`#${pad(serieId, 2)}`}
									src={`${process.env.PUBLIC_URL}/images/${
										listOption === "f" ? "f" : "m"
									}/top21/${serieId}.jpg`}
									sx={{ width: 64, height: 64 }}
								/>
							</ListItemIcon>
							<Typography>#{pad(serieId, 2)}</Typography>
						</Stack>

						<ListItemSecondaryAction>
							<Typography>{listOption === "f" ? "🌸" : "🍀"}</Typography>
						</ListItemSecondaryAction>
					</ListSubheader>
					<ListItem
						secondaryAction={<Typography>{`${formattedValue}`}</Typography>}
					>
						<Stack direction="row" alignItems="center" spacing={2}>
							<Typography>VS</Typography>
							<ListItemIcon>
								<Avatar
									alt={`#${pad(vs.x, 2)}`}
									src={`${process.env.PUBLIC_URL}/images/${
										listOption === "f" ? "f" : "m"
									}/top21/${vs.x}.jpg`}
									sx={{ width: 64, height: 64 }}
								/>
							</ListItemIcon>
							<Typography>#{pad(vs.x, 2)}</Typography>
						</Stack>
					</ListItem>
				</List>
			</Paper>
		);
	};

	const chartProps = {
		width:
			localData.length * 120 > (width >= 1080 ? width - sidebarWidth : width)
				? localData.length * 120
				: width >= 1080
				? width - sidebarWidth - 120
				: width - 120,
		height: height - 20,
		data: localData,
		theme: {
			textColor: darkModeEnabled ? "#FFF" : "#000",
			legends: { title: { text: darkModeEnabled ? "#FFF" : "#000" } },
			labels: {
				text: {
					fontSize: 16,
				},
			},
		},
		margin: { top: 100, right: 60, bottom: 60, left: 60 },
		valueFormat: ">+0.2~f",
		label: ({ formattedValue, data }) => {
			const v = Math.abs(data.y);
			return formattedValue === null
				? ""
				: v > 100
				? `${formattedValue}`
				: v > 50
				? `${formattedValue} 🤏`
				: v > 25
				? `${formattedValue} 🙀`
				: `${formattedValue} 😱`;
		},
		labelTextColor: ({ data }) => {
			if (data.y === null) return null;

			const v = Math.abs(data.y);

			return v > 0.0425 ? "#EEE" : "#666";
		},
		axisTop: {
			tickSize: 5,
			tickPadding: 5,
			tickRotation: 0,
			legend: "",
		},
		axisRight: {
			tickSize: 5,
			tickPadding: 5,
			tickRotation: 0,
			legend: "",
		},
		axisLeft: {
			tickSize: 5,
			tickPadding: 5,
			tickRotation: 0,
			legend: "",
		},
		axisBottom: {
			tickSize: 5,
			tickPadding: 5,
			tickRotation: 0,
			legend: "",
		},
		colors: {
			type: "diverging",
			scheme: "red_yellow_green",
			minValue: -0.0975,
			maxValue: 0.0975,
			divergeAt: 0.5,
		},
		emptyColor: "#CCC",
		legends: [],
		tooltip: ChartTooltip,
	};

	return (
		<>
			<Box
				width={width >= 1080 ? width - sidebarWidth : width}
				height={height}
				display="flex"
			>
				<HeatMap
					{...chartProps}
					layers={["axes"]}
					width={60}
					axisRight={null}
					axisTop={null}
					axisBottom={null}
				/>
				<Box
					sx={{ overflowX: "scroll", overflowY: "hidden" }}
					width={width >= 1080 ? width - sidebarWidth : width}
					height={height - 20}
				>
					<HeatMap
						{...chartProps}
						axisLeft={null}
						axisRight={null}
						margin={{ ...chartProps.margin, left: 0, right: 0 }}
					/>
				</Box>
				<HeatMap
					{...chartProps}
					layers={["axes"]}
					width={60}
					axisLeft={null}
					axisTop={null}
					axisBottom={null}
					margin={{ ...chartProps.margin, left: 0 }}
				/>
			</Box>
		</>
	);
};

export default ComparePage;
